import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, Mail } from 'lucide-react';
import { toast } from 'sonner';

const AdminLogin = ({ onSuccess }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        const result = await register(formData.username, formData.email, formData.password);
        if (result.success) {
          toast.success('Registration successful! Please login.');
          setIsRegister(false);
          setFormData({ username: '', email: '', password: '' });
        } else {
          toast.error(result.error);
        }
      } else {
        const result = await login(formData.username, formData.password);
        if (result.success) {
          toast.success('Login successful!');
          onSuccess();
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass p-8 rounded-lg border border-white/10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl text-gold mb-2">
              Admin {isRegister ? 'Register' : 'Login'}
            </h1>
            <p className="text-white/60 text-sm">
              Vikki Wright Music Composer
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm text-white/60 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded px-10 py-3 text-white focus:outline-none focus:border-gold/50 transition-colors"
                  placeholder="Enter username"
                />
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm text-white/60 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded px-10 py-3 text-white focus:outline-none focus:border-gold/50 transition-colors"
                    placeholder="Enter email"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm text-white/60 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded px-10 py-3 text-white focus:outline-none focus:border-gold/50 transition-colors"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold hover:bg-gold/80 text-black font-bold py-3 rounded transition-colors disabled:opacity-50"
            >
              {loading ? 'Please wait...' : (isRegister ? 'Register' : 'Login')}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-white/60 hover:text-gold transition-colors"
            >
              {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
